import { useMemo } from 'react'
import { useProject } from '../stores/ProjectContext'
import { useI18n } from '../stores/I18nContext'
import './InsightsBar.css'

export default function InsightsBar({ onConflictsClick }) {
  const { project } = useProject()
  const { language } = useI18n()
  const isNorwegian = language === 'nb'

  const insights = useMemo(() => {
    const flows = project?.flows || []
    const allCheckpoints = flows.flatMap(f => f.checkpoints || [])

    // Count completed checkpoints
    const completed = allCheckpoints.filter(cp => cp.status === 'completed').length
    const total = allCheckpoints.length

    // Find conflicts (checkpoints at same time/distance)
    let conflicts = 0
    for (let i = 0; i < allCheckpoints.length; i++) {
      for (let j = i + 1; j < allCheckpoints.length; j++) {
        const a = allCheckpoints[i]
        const b = allCheckpoints[j]
        if (a.time === b.time && a.distance === b.distance) {
          conflicts++
        }
      }
    }

    // Find max waiting time between checkpoints in same flow
    let maxWait = 0
    flows.forEach(flow => {
      const sorted = [...(flow.checkpoints || [])].sort((a, b) => a.time - b.time)
      for (let i = 1; i < sorted.length; i++) {
        const wait = sorted[i].time - sorted[i - 1].time
        if (wait > maxWait) maxWait = wait
      }
    })

    // Calculate critical path (longest flow)
    let criticalPath = { name: '', duration: 0 }
    flows.forEach(flow => {
      const sorted = [...(flow.checkpoints || [])].sort((a, b) => a.time - b.time)
      if (sorted.length > 0) {
        const duration = sorted[sorted.length - 1].time - sorted[0].time
        if (duration > criticalPath.duration) {
          criticalPath = { name: flow.name, duration }
        }
      }
    })

    return {
      flowCount: flows.length,
      completed,
      total,
      conflicts,
      maxWait,
      criticalPath
    }
  }, [project])

  const timeUnit = project?.timeUnit || 'weeks'
  const timeUnitLabel = isNorwegian
    ? (timeUnit === 'weeks' ? 'uker' : timeUnit === 'days' ? 'dager' : timeUnit)
    : (timeUnit === 'weeks' ? 'weeks' : timeUnit === 'days' ? 'days' : timeUnit)

  return (
    <div className="insights-bar">
      <div className="insights-items">
        <div className="insight-item">
          <span className="insight-icon">📊</span>
          <span>{insights.flowCount} {isNorwegian ? 'flyter' : 'flows'}</span>
        </div>

        <div className="insight-item">
          <span className="insight-icon">✓</span>
          <span>{insights.completed}/{insights.total} {isNorwegian ? 'fullført' : 'completed'}</span>
        </div>

        {insights.conflicts > 0 && (
          <div
            className="insight-item warning clickable"
            onClick={onConflictsClick}
            title={isNorwegian ? 'Klikk for å se detaljer' : 'Click to view details'}
          >
            <span className="insight-icon">⚠️</span>
            <span>{insights.conflicts} {isNorwegian ? 'konflikter' : 'conflicts'}</span>
          </div>
        )}

        {insights.maxWait > 0 && (
          <div className="insight-item">
            <span className="insight-icon">⏱️</span>
            <span>{insights.maxWait} {timeUnitLabel} {isNorwegian ? 'maks ventetid' : 'max wait'}</span>
          </div>
        )}

        {insights.criticalPath.name && (
          <div className="insight-item">
            <span className="insight-icon">🛤️</span>
            <span>{isNorwegian ? 'Kritisk sti' : 'Critical path'}: {insights.criticalPath.name}</span>
          </div>
        )}
      </div>
    </div>
  )
}
